/**
 * Satellite hot spots from Digital Earth Australia Hotspots (Geoscience Australia; docs/specs/map.md §30; decision
 * 796): the second fire layer, beside the state agencies' incidents (`fire-incidents.ts`).
 *
 * DEA Hotspots is free, "released under the Creative Commons Attribution 4.0 International Licence" with the credit
 * "© Commonwealth of Australia (Geoscience Australia)", "updated with new information every 10 minutes", and "is not
 * to be used for safety of life decisions" (its WFS capabilities and product page, read 27 Oct 2026). A hot spot is a
 * satellite's reading of unusual heat, not an incident report: it can be a fire, a burn-off, a gas flare or hot
 * ground, and an hour or more old. So the layer is information only, in its own colours (not the warning levels'),
 * with its age on every marker.
 *
 * Teslyn Field's Rust core asks the WFS (`hotspots.rs`) for the last 24 hours in a box of whole half degrees around
 * the map view, at most 4 by 4 degrees ("the box"), each box at most once in 10 minutes. Here the answer is read, the
 * hot spots are gathered into one marker per square of about a kilometre (satellites see the same fire many times a
 * day), and the markers are coloured by the age of the newest detection in the square.
 */
import type { GeoBounds } from './map-geo.js';
import type { LatLng } from './planning.js';
import type { LegendEntry, MapFeature } from './map-layers.js';
import type { IdentifyRow } from './map-kml.js';

const MIN = 60_000;
const HOUR = 60 * MIN;

/** The source's name, licence and credit (the WFS's access constraints), and its public site. */
export const HOTSPOT_SOURCE = {
  name: 'DEA Hotspots (Geoscience Australia)',
  licence: 'CC BY 4.0',
  credit: 'Satellite hot spots: © Commonwealth of Australia (Geoscience Australia), DEA Hotspots, CC BY 4.0',
  site: 'https://hotspots.dea.ga.gov.au/',
} as const;

/** The notice on the layer and on every hot spot (decision 796; DEA's own "not for safety of life decisions"). */
export const HOTSPOT_NOTICE =
  'Information only, not a safety system. A hot spot is a satellite’s reading of heat, not a confirmed fire: it can be a burn-off, a flare or hot ground, and hours old. DEA Hotspots is not to be used for safety of life decisions; follow the fire agencies’ warnings and your company’s procedure.';

/** The hours of hot spots asked and drawn. */
export const HOTSPOT_HOURS = 24;
/** DEA updates every 10 minutes: one box is asked at most this often. */
export const HOTSPOT_EVERY_MS = 10 * MIN;
/** The box asked is at most this many degrees each way, around the view's centre. */
export const HOTSPOT_MAX_BOX_DEG = 4;
/** The box's edges are whole multiples of this (small pans ask nothing new). */
const SNAP_DEG = 0.5;
/** Hot spots are gathered in squares of this size (about 1.1 km north to south). */
export const HOTSPOT_CELL_DEG = 0.01;
/** The most hot spots one answer holds (the Rust core asks for no more); a full answer says so. */
export const HOTSPOT_MAX_FEATURES = 20_000;

/** Where DEA Hotspots has data: Australia and its waters (the box is kept inside). */
const AUSTRALIA: GeoBounds = { south: -45, west: 108, north: -9, east: 157 };

export interface HotspotBox {
  readonly south: number;
  readonly west: number;
  readonly north: number;
  readonly east: number;
}

const snapDown = (v: number): number => Math.floor(v / SNAP_DEG) * SNAP_DEG;
const snapUp = (v: number): number => Math.ceil(v / SNAP_DEG) * SNAP_DEG;
const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

/**
 * The box to ask for a view: the view, at most 4 by 4 degrees around its centre, widened to whole half degrees and
 * kept inside Australia; null when the view is not over Australia.
 */
export function hotspotBox(view: GeoBounds): HotspotBox | null {
  const vals = [view.south, view.west, view.north, view.east];
  if (!vals.every(Number.isFinite) || view.north <= view.south || view.east <= view.west) return null;
  const half = HOTSPOT_MAX_BOX_DEG / 2;
  const lat = (view.south + view.north) / 2;
  const lng = (view.west + view.east) / 2;
  let south = Math.max(view.south, lat - half);
  let north = Math.min(view.north, lat + half);
  let west = Math.max(view.west, lng - half);
  let east = Math.min(view.east, lng + half);
  south = clamp(snapDown(south), AUSTRALIA.south, AUSTRALIA.north);
  north = clamp(snapUp(north), AUSTRALIA.south, AUSTRALIA.north);
  west = clamp(snapDown(west), AUSTRALIA.west, AUSTRALIA.east);
  east = clamp(snapUp(east), AUSTRALIA.west, AUSTRALIA.east);
  // widening to half degrees can pass 4 degrees: trim the far edge back
  if (north - south > HOTSPOT_MAX_BOX_DEG) north = south + HOTSPOT_MAX_BOX_DEG;
  if (east - west > HOTSPOT_MAX_BOX_DEG) east = west + HOTSPOT_MAX_BOX_DEG;
  if (north <= south || east <= west) return null;
  return { south, west, north, east };
}

/** Whether one box lies inside another (a view still inside the box asked asks nothing new). */
export function hotspotBoxInside(inner: HotspotBox, outer: HotspotBox): boolean {
  return (
    inner.south >= outer.south &&
    inner.north <= outer.north &&
    inner.west >= outer.west &&
    inner.east <= outer.east
  );
}

/** DEA's fields Teslyn shows (the WFS also sends ids, file names and processing times). */
const HOTSPOT_FIELDS =
  'satellite,sensor,product,datetime,temp_kelvin,power,confidence,australian_state,accuracy,location';

/**
 * The one address asked for a box: DEA's public WFS, the last 24 hours, newest first, DEA's fields only, at most
 * `HOTSPOT_MAX_FEATURES`. The same address Teslyn Field's Rust core builds (`HotspotBox::url` in `hotspots.rs`; both
 * are tested against the genuine capture's), here for Teslyn Cloud's fire poller. The CQL filter is latitude first,
 * as DEA's GeoServer reads EPSG:4326, and percent-encoded by hand: only digits, '.' and '-' come from the box.
 */
export function hotspotWfsUrl(b: HotspotBox): string {
  const n = (v: number): string => v.toFixed(1);
  const cql =
    `hours_since_hotspot%3C%3D${HOTSPOT_HOURS}%20AND%20BBOX%28location%2C` +
    `${n(b.south)}%2C${n(b.west)}%2C${n(b.north)}%2C${n(b.east)}%29`;
  return (
    'https://hotspots.dea.ga.gov.au/geoserver/public/wfs?service=WFS&version=1.1.0&request=GetFeature' +
    '&typeName=public:hotspots_three_days&outputFormat=application/json&srsName=EPSG:4326' +
    `&propertyName=${HOTSPOT_FIELDS}&sortBy=datetime+D&maxFeatures=${HOTSPOT_MAX_FEATURES}&CQL_FILTER=${cql}`
  );
}

export const hotspotBoxKey = (b: HotspotBox): string => `${b.south},${b.west},${b.north},${b.east}`;

// ------------------------------------------------------------------------------------------------ reading

/** One detection as DEA published it. */
export interface Hotspot {
  readonly at: LatLng;
  readonly satellite: string;
  readonly sensor: string;
  readonly product: string;
  /** When the satellite saw it (ISO, UTC). */
  readonly observedAt: string;
  readonly tempK: number | null;
  /** Fire radiative power, megawatts. */
  readonly powerMw: number | null;
  /** The product's own confidence, per cent. */
  readonly confidence: number | null;
  readonly state: string;
  /** DEA's words for the position's accuracy ("± 2km"). */
  readonly accuracy: string;
}

export interface ParsedHotspots {
  readonly hotspots: Hotspot[];
  /** The answer held as many as were asked for: there may be more. */
  readonly full: boolean;
  readonly problem: string | null;
}

type Json = Record<string, unknown>;
const isObj = (v: unknown): v is Json => typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** The WFS answer (GeoJSON points) as hot spots; anything without a place or a time is left out. */
export function parseHotspots(body: string): ParsedHotspots {
  let doc: unknown;
  try {
    doc = JSON.parse(body);
  } catch {
    return { hotspots: [], full: false, problem: 'DEA Hotspots sent an answer that could not be read.' };
  }
  if (!isObj(doc) || !Array.isArray(doc.features))
    return { hotspots: [], full: false, problem: 'DEA Hotspots did not send a list of hot spots.' };
  const out: Hotspot[] = [];
  for (const f of doc.features) {
    if (!isObj(f) || !isObj(f.properties) || !isObj(f.geometry)) continue;
    const c = f.geometry.coordinates;
    if (f.geometry.type !== 'Point' || !Array.isArray(c)) continue;
    const lng = num(c[0]);
    const lat = num(c[1]);
    const p = f.properties;
    const t = Date.parse(str(p.datetime));
    if (lat === null || lng === null || Math.abs(lat) > 90 || Math.abs(lng) > 180 || !Number.isFinite(t))
      continue;
    out.push({
      at: { lat, lng },
      satellite: str(p.satellite),
      sensor: str(p.sensor),
      product: str(p.product),
      observedAt: new Date(t).toISOString(),
      tempK: num(p.temp_kelvin),
      powerMw: num(p.power),
      confidence: num(p.confidence),
      state: str(p.australian_state),
      accuracy: str(p.accuracy),
    });
  }
  return { hotspots: out, full: doc.features.length >= HOTSPOT_MAX_FEATURES, problem: null };
}

// ------------------------------------------------------------------------------------------------ squares

/** The hot spots of one square of about a kilometre: the newest detection, how many, and by which satellites. */
export interface HotspotCell {
  readonly key: string;
  readonly newest: Hotspot;
  readonly count: number;
  /** "NOAA 21 (VIIRS)" and so on, in the order first seen. */
  readonly satellites: readonly string[];
  /** The oldest detection in the square (ISO). */
  readonly firstAt: string;
}

const cellKey = (p: LatLng): string =>
  `${Math.round(p.lat / HOTSPOT_CELL_DEG)}:${Math.round(p.lng / HOTSPOT_CELL_DEG)}`;

const satelliteWords = (h: Hotspot): string => (h.sensor ? `${h.satellite} (${h.sensor})` : h.satellite);

/** One marker per square: the newest detection is shown, at its own position. */
export function hotspotCells(hotspots: readonly Hotspot[]): HotspotCell[] {
  const by = new Map<string, { newest: Hotspot; count: number; sats: string[]; first: string }>();
  for (const h of hotspots) {
    const k = cellKey(h.at);
    const c = by.get(k);
    if (!c) {
      by.set(k, { newest: h, count: 1, sats: [satelliteWords(h)], first: h.observedAt });
      continue;
    }
    c.count += 1;
    if (h.observedAt > c.newest.observedAt) c.newest = h;
    if (h.observedAt < c.first) c.first = h.observedAt;
    const s = satelliteWords(h);
    if (!c.sats.includes(s)) c.sats.push(s);
  }
  return [...by.entries()].map(([key, c]) => ({
    key,
    newest: c.newest,
    count: c.count,
    satellites: c.sats,
    firstAt: c.first,
  }));
}

// ------------------------------------------------------------------------------------------------ the layer

/** Hot spot colours (`brand/tokens/tokens.json` group `hotspots`): by age, never the warning levels' colours. */
export type HotspotColour = 'hotspot-new' | 'hotspot-recent' | 'hotspot-older';

export const HOTSPOT_TOKENS: readonly HotspotColour[] = ['hotspot-new', 'hotspot-recent', 'hotspot-older'];

const AGE_WORD: Readonly<Record<HotspotColour, string>> = {
  'hotspot-new': 'Hot spot, under 2 hours old',
  'hotspot-recent': 'Hot spot, 2 to 6 hours old',
  'hotspot-older': 'Hot spot, 6 to 24 hours old',
};

/** The colour of a detection by its age; null when it is over 24 hours old (not drawn). */
export function hotspotAge(observedAt: string, nowMs: number): HotspotColour | null {
  const age = nowMs - Date.parse(observedAt);
  if (!Number.isFinite(age) || age > HOTSPOT_HOURS * HOUR) return null;
  if (age < 2 * HOUR) return 'hotspot-new';
  if (age < 6 * HOUR) return 'hotspot-recent';
  return 'hotspot-older';
}

/** "1 h 20 min ago", "just now". */
function ageText(at: string, nowMs: number): string {
  const mins = Math.max(0, Math.floor((nowMs - Date.parse(at)) / MIN));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  return `${Math.floor(mins / 60)} h ${mins % 60} min ago`;
}

const utc = (iso: string): string => `${iso.slice(0, 16).replace('T', ' ')} UTC`;

export interface HotspotLayer {
  readonly features: MapFeature[];
  readonly notDrawn: string[];
  readonly legend: LegendEntry[];
  /** Squares drawn, by age. */
  readonly counts: Readonly<Record<HotspotColour, number>>;
  /** Detections in the squares drawn. */
  readonly detections: number;
  readonly credits: string[];
}

/**
 * The hot spot layer: a marker per square in its age's colour, the oldest first so the newest draw on top. The
 * identify rows are DEA's own values for the newest detection, the square's count and satellites, the source, the
 * credit and the notice.
 */
export function hotspotFeatures(cells: readonly HotspotCell[], nowMs: number): HotspotLayer {
  const counts: Record<HotspotColour, number> = { 'hotspot-new': 0, 'hotspot-recent': 0, 'hotspot-older': 0 };
  let detections = 0;
  const drawn: { cell: HotspotCell; colour: HotspotColour }[] = [];
  for (const cell of cells) {
    const colour = hotspotAge(cell.newest.observedAt, nowMs);
    if (!colour) continue;
    drawn.push({ cell, colour });
    counts[colour] += 1;
    detections += cell.count;
  }
  drawn.sort((a, b) => (a.cell.newest.observedAt < b.cell.newest.observedAt ? -1 : 1));
  const features: MapFeature[] = drawn.map(({ cell, colour }) => {
    const h = cell.newest;
    const ago = ageText(h.observedAt, nowMs);
    const identify: IdentifyRow[] = [
      { label: 'Satellite hot spot', value: `Seen ${utc(h.observedAt)}, ${ago}` },
      {
        label: 'Detections in this square (about 1 km)',
        value:
          cell.count === 1
            ? '1 in the last 24 hours'
            : `${cell.count} in the last 24 hours, the first ${utc(cell.firstAt)}`,
      },
      { label: 'Satellites', value: cell.satellites.join(', ') },
      ...(h.product ? [{ label: 'Product', value: h.product }] : []),
      ...(h.tempK !== null ? [{ label: 'Brightness temperature', value: `${h.tempK} K` }] : []),
      ...(h.powerMw !== null ? [{ label: 'Fire radiative power', value: `${h.powerMw} MW` }] : []),
      ...(h.confidence !== null ? [{ label: 'Confidence (the product’s)', value: `${h.confidence} %` }] : []),
      ...(h.accuracy ? [{ label: 'Position accuracy', value: h.accuracy }] : []),
      ...(h.state ? [{ label: 'State', value: h.state }] : []),
      { label: 'Source', value: `${HOTSPOT_SOURCE.name} (${HOTSPOT_SOURCE.licence})` },
      { label: 'Credit', value: HOTSPOT_SOURCE.credit },
      { label: 'Note', value: HOTSPOT_NOTICE },
    ];
    return {
      id: cell.key,
      layer: 'hotspots',
      geometry: { type: 'point', at: h.at },
      // a flame in the age's colour (R143)
      style: { colour, stroke: 'normal', dashed: false, fill: 0.9, symbol: 'fire' },
      tooltip: `Satellite hot spot · ${ago}${cell.count > 1 ? ` · ${cell.count} detections` : ''}`,
      identify,
      link: { url: HOTSPOT_SOURCE.site, label: 'Open DEA Hotspots' },
    };
  });
  const legend: LegendEntry[] = HOTSPOT_TOKENS.filter((c) => counts[c] > 0).map((c) => ({
    colour: c,
    stroke: 'normal',
    dashed: false,
    word: AGE_WORD[c],
    symbol: 'fire',
  }));
  return {
    features,
    notDrawn: [],
    legend,
    counts,
    detections,
    credits: features.length > 0 ? [HOTSPOT_SOURCE.credit] : [],
  };
}

// ------------------------------------------------------------------------------------------- the laptop's copy

/** The last answer's squares, kept on the laptop so hot spots show offline and after a restart, with their age. */
export interface HotspotCopy {
  readonly box: HotspotBox;
  /** When DEA answered (ISO). */
  readonly fetchedAt: string;
  readonly cells: readonly HotspotCell[];
  readonly full: boolean;
}

/** A copy older than the hours drawn shows nothing: it is not kept. */
export const HOTSPOT_COPY_MAX_AGE_MS = HOTSPOT_HOURS * HOUR;

const isBox = (v: unknown): v is HotspotBox =>
  isObj(v) &&
  ['south', 'west', 'north', 'east'].every((k) => typeof v[k] === 'number' && Number.isFinite(v[k]));

function readHotspot(v: unknown): Hotspot | null {
  if (!isObj(v) || !isObj(v.at)) return null;
  const lat = num(v.at.lat);
  const lng = num(v.at.lng);
  if (lat === null || lng === null || !Number.isFinite(Date.parse(str(v.observedAt)))) return null;
  return {
    at: { lat, lng },
    satellite: str(v.satellite),
    sensor: str(v.sensor),
    product: str(v.product),
    observedAt: str(v.observedAt),
    tempK: num(v.tempK),
    powerMw: num(v.powerMw),
    confidence: num(v.confidence),
    state: str(v.state),
    accuracy: str(v.accuracy),
  };
}

/** A stored copy as written by `hotspotCells`, or null when it is not one. */
export function readHotspotCopy(raw: unknown): HotspotCopy | null {
  if (
    !isObj(raw) ||
    !isBox(raw.box) ||
    !Number.isFinite(Date.parse(str(raw.fetchedAt))) ||
    !Array.isArray(raw.cells)
  )
    return null;
  const cells: HotspotCell[] = [];
  for (const c of raw.cells) {
    if (!isObj(c) || typeof c.key !== 'string' || typeof c.count !== 'number' || !Array.isArray(c.satellites))
      continue;
    const newest = readHotspot(c.newest);
    if (!newest) continue;
    cells.push({
      key: c.key,
      newest,
      count: c.count,
      satellites: c.satellites.filter((s): s is string => typeof s === 'string'),
      firstAt: Number.isFinite(Date.parse(str(c.firstAt))) ? str(c.firstAt) : newest.observedAt,
    });
  }
  return { box: raw.box, fetchedAt: str(raw.fetchedAt), cells, full: raw.full === true };
}
